import React, {useImperativeHandle} from 'react';
import { RoleMasterForm as RoleMasterFormType } from "../../../models/config/RoleMaster";
import {forwardRef, useCallback, useMemo, useState} from "react";
import {useCompanyMaster} from "../../../queries";
import {CompanyMaster} from "../../../models/companymaster/CompanyMaster";
import {useMenuMaster} from "../../LegacyMenuMasterConfig/queries";
import {MenuMaster} from "../../../models/config/MenuMaster";
import Select, { ActionMeta, MultiValue, SingleValue } from 'react-select';
import {Form, FormFeedback, FormGroup, Input, Label} from "reactstrap";
import { twMerge } from 'tailwind-merge';
import { useUserInformation } from 'hooks';

export type RoleMasterFormRefHandle = {
    submit: () => RoleMasterFormType | undefined;
};

export type RoleMasterFormProps = {
    disabledKeyField?: boolean;
    defaultValues?: RoleMasterFormType;
    className?: string;
    onSubmit?: (formValues: RoleMasterFormType) => void;
};

export const RoleMasterForm = forwardRef<
    RoleMasterFormRefHandle,
    RoleMasterFormProps
>((props, ref) => {
    const { disabledKeyField, defaultValues, className } = props;

    const [formValues, setFormValues] = useState<RoleMasterFormType>({
        sid: defaultValues?.sid ?? '',
        id: defaultValues?.id ?? '',
        roleDetail: defaultValues?.roleDetail ?? '',
        allowMenus: defaultValues?.allowMenus ?? []
    });
    const [errors, setErrors] = useState<Record<keyof RoleMasterFormType, string>>(
        // @ts-ignore
        {}
    );

    const { data: companyMaster } = useCompanyMaster();

	const { companyOptions } = useUserInformation()
    // const companyOptions = useMemo(() => {
    //     if (!companyMaster) {
    //         return undefined;
    //     }

    //     const selectAllOption = {
    //         sid: '*',
    //         company_name: '* : SUPER ADMIN'
    //     } as CompanyMaster;
    //     return [selectAllOption, ...companyMaster];
    // }, [companyMaster]);

    const { data: menuMaster } = useMenuMaster({
        enabled: !!formValues.sid,
        sid: formValues.sid
    });
    const menuOptions = useMemo(() => {
        const selectAllOptions = { id: 'เลือกทั้งหมด' } as MenuMaster;
        return menuMaster ? [selectAllOptions, ...menuMaster] : undefined;
    }, [menuMaster]);

    const getMenuMasterLabel = useCallback(
        (menuMaster: MenuMaster) =>
            menuMaster.id +
            (menuMaster.menu_name ? `: ${menuMaster.menu_name}` : ''),
        []
    );

    const getMenuMasterValue = useCallback(
        (menuMaster: MenuMaster) => menuMaster.id,
        []
    );

    const handleFormValueChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const key = event.currentTarget.name;
        const value = event.currentTarget.value;

        setFormValues({ ...formValues, [key]: value });
    };

    const handleSelectedCompany = (
        newSelectedCompany: SingleValue<CompanyMaster>
    ) => {
        if (!newSelectedCompany) {
            return;
        }

        setFormValues({
            ...formValues,
            sid: newSelectedCompany.sid
        });
    };

    const handleSelectedMenusChange = (
        newSelectedMenus: MultiValue<MenuMaster>,
        actionMeta: ActionMeta<MenuMaster>
    ) => {
        if (
            actionMeta.action === 'select-option' &&
            actionMeta.option?.id === 'เลือกทั้งหมด'
        ) {
            setFormValues({
                ...formValues,
                allowMenus: menuOptions?.slice(1) ?? []
            });
            return;
        }

        setFormValues({
            ...formValues,
            // @ts-ignore
            allowMenus: newSelectedMenus
        });
    };

    const submitForm = () => {
        const errors: [keyof RoleMasterFormType, string][] = [];

        if (!formValues.sid) {
            errors.push(['sid', 'กรุณาเลือกบริษัท']);
        }

        if (!formValues.id) {
            errors.push(['id', 'กรุณากรอก Role ID']);
        }

        if (errors.length > 0) {
            setErrors(prevState => ({
                ...prevState,
                // @ts-ignore
                ...Object.fromEntries(errors)
            }));

            return undefined;
        }

        return { ...formValues };
    };

    useImperativeHandle(
        ref,
        () => ({
            submit: submitForm
        }),
        [submitForm]
    );

    return (
        <Form className={twMerge('tw-flex tw-flex-col', className)}>
            <FormGroup>
                <Label for="sid">Company</Label>
                <Select
                    isDisabled={disabledKeyField}
                    options={companyOptions}
                    value={companyOptions?.find(
                        companyOption => companyOption.sid === formValues.sid
                    )}
                    getOptionLabel={(company: CompanyMaster) =>
                        company.company_name
                    }
                    getOptionValue={(company: CompanyMaster) => company.sid}
                    onChange={handleSelectedCompany}
                />
                <FormFeedback>{errors.sid}</FormFeedback>
            </FormGroup>
            <FormGroup>
                <Label for="id">Role ID</Label>
                <Input
                    disabled={disabledKeyField}
                    invalid={!!errors.id}
                    id="id"
                    type="text"
                    name="id"
                    value={formValues.id}
                    onChange={
                        !disabledKeyField ? handleFormValueChange : undefined
                    }
                />
                <FormFeedback>{errors.id}</FormFeedback>
            </FormGroup>
            <FormGroup>
                <Label for="menu_name">Allowed Menu</Label>
                <Select
                    isMulti
                    isDisabled={!formValues.sid}
                    options={menuOptions}
                    value={formValues.allowMenus}
                    getOptionLabel={getMenuMasterLabel}
                    getOptionValue={getMenuMasterValue}
                    onChange={handleSelectedMenusChange}
                />
                <FormFeedback>{errors.allowMenus}</FormFeedback>
            </FormGroup>
        </Form>
    );
});